/**
 * How much of production Sentry actually records.
 *
 * This existed as the literal `tracesSampleRate: 1` in FIVE places — web's
 * server, edge and client configs, `apps/api/src/sentry.ts` and
 * `apps/realtime/src/sentry.ts`. That is the Sentry scaffold's default, and it
 * was never turned down: every request in every runtime produced a full
 * transaction with spans, in production and in Preview both (the only exclusion
 * anywhere is `NODE_ENV === "development"`).
 *
 * Five independent literals are also why it went unnoticed while two more
 * runtimes were added during the API extraction — each new `sentry.ts` copied
 * the scaffold rather than a decision. One module means the next runtime cannot
 * quietly disagree.
 *
 * ## Tuning this without a deploy
 *
 * `SENTRY_TRACES_SAMPLE_RATE` overrides the default, so the rate can be moved
 * from the Vercel dashboard once there is an invoice to read. The browser needs
 * `NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE` instead — client bundles only receive
 * `NEXT_PUBLIC_`-prefixed variables — which is why this takes the raw string and
 * each caller supplies the variable its runtime can actually see.
 *
 * A malformed or out-of-range value falls back to the default rather than
 * throwing: a typo in an env var must not be able to stop a deployment from
 * reporting errors, which is the one thing Sentry is there for. Note that
 * sampling applies to TRACES only — errors are always sent.
 */

/**
 * 10%, which is the usual starting point for a service of this size and enough
 * to keep latency percentiles meaningful. It is a starting point and not a
 * finding: nobody has read the invoice yet. If traces turn out to be the line
 * item, a `tracesSampler` keeping 100% of errors and slow routes and near-zero
 * of health checks is the next step up in precision.
 */
export const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

/**
 * Resolves the trace sample rate from a raw environment value.
 *
 * @param raw - the value of `SENTRY_TRACES_SAMPLE_RATE`, or its
 *   `NEXT_PUBLIC_`-prefixed twin in a browser bundle.
 */
export function sukhaTracesSampleRate(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === "") return DEFAULT_TRACES_SAMPLE_RATE;

  const parsed = Number(raw);
  // `Number("")` is 0 and `Number("abc")` is NaN — the empty case is handled
  // above so that an unset-but-present variable does not silently mean "record
  // nothing", which would look exactly like Sentry being broken.
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return DEFAULT_TRACES_SAMPLE_RATE;
  }

  return parsed;
}
